import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { siQuoteUrls } from '../../../environments/environment';
import {
  SapSubmittalPrerequisiteRequest,
  SapSubmittalPrerequisitesResponse,
  SapSubmittalRequest,
  SapSubmittalResponse,
  OpenEstimateResponse,
  UnlockSapSubmittalRequest
} from '../../models/dtos';
import { BaseHttpService, StreamEvent } from './base-http.service';
import { withBackgroundRequest, withLocalErrorHandling } from '../../core/errors/http-context-tokens';

/**
 * Progress event emitted while the SAP submittal runs on the server.
 * 'status' carries a step description and 'result' the final response.
 */
export type SapSubmittalEvent = StreamEvent<SapSubmittalResponse>;

/**
 * API service for SAP Submittal operations.
 * Handles communication with the SapSubmittal backend controller.
 */
@Injectable({ providedIn: 'root' })
export class SapSubmittalApiService {

  constructor(private readonly baseHttp: BaseHttpService) { }

  /**
   * Fetches SAP submittal prerequisites including restriction flags,
   * proposal ID, and summary estimate parameters.
   * Backend also validates GOC restriction and collaborative estimate state.
   */
  getPrerequisites(request: SapSubmittalPrerequisiteRequest): Observable<SapSubmittalPrerequisitesResponse> {
    const context: HttpContext = withLocalErrorHandling();
    return this.baseHttp.post<SapSubmittalPrerequisitesResponse>(
      `${siQuoteUrls.sapSubmittal}/GetSapSubmittalPreRequisites`, request, { context });
  }

  /**
   * Submits the estimate to SAP. Performs server-side validation (negative values,
   * labor hours/cost mismatch, parts quantity), creates XML, uploads, and saves.
   * Streams progress updates back as Server-Sent Events while the submittal runs.
   */
  submitEstimate(request: SapSubmittalRequest): Observable<SapSubmittalEvent> {
    const context: HttpContext = withBackgroundRequest();
    return this.baseHttp.postTypedStream<SapSubmittalResponse>(`${siQuoteUrls.sapSubmittal}/SapSubmitEstimate`, request, { context });
  }

  /**
   * Unlocks a submitted estimate for resubmission.
  * Creates a clone of the estimate, resets its status and returns the reopened estimate.
   */
  unlockSubmittal(request: UnlockSapSubmittalRequest): Observable<OpenEstimateResponse> {
    const context: HttpContext = withLocalErrorHandling();
    return this.baseHttp.post<OpenEstimateResponse>(
      `${siQuoteUrls.sapSubmittal}/UnlockSapSubmittal`, request, { context });
  }
}
